import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  Divider,
  InputAdornment,
  Tabs,
  Tab,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import Chip from "../../../components/Chip";
import SearchIcon from "../../../icons/Search";

const ModalContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const ModalHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const SelectedCountChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontWeight: "bold",
}));

const ButtonGroup = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
}));

const ColumnList = styled(Box)(({ theme }) => ({
  maxHeight: 400,
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const ColumnItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1),
  cursor: "pointer",
  padding: theme.spacing(1),
  borderRadius: "4px",
  marginLeft: theme.spacing(-1),
  marginRight: theme.spacing(-1),
  userSelect: "none",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
}));

const ColumnDetails = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

const ColumnType = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.75rem",
}));

const ModalFooter = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

type PropertyInfo = {
  field: string;
  typeName: string;
  fieldName: string;
  type: "Attribute" | "Parameter";
};

type PropertyDisplayInfo = {
  typeName: string;
  fieldName: string;
  type: "Attribute" | "Parameter";
};

type ConditionInfo = {
  id: number;
  name: string;
};

type GridViewFilterModalProps = {
  open: boolean;
  onClose: () => void;
  onApply: (
    visibleColumns: Set<string>,
    visibleConditions: Set<number>
  ) => void;
  allPropertyNames: string[];
  propertyDisplayInfo: Map<string, PropertyDisplayInfo>;
  initialVisibleColumns: Set<string>;
  conditions: ConditionInfo[];
  initialVisibleConditions: Set<number>;
};

const GridViewFilterModal = ({
  open,
  onClose,
  onApply,
  allPropertyNames,
  propertyDisplayInfo,
  initialVisibleColumns,
  conditions,
  initialVisibleConditions,
}: GridViewFilterModalProps) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(
    new Set(initialVisibleColumns)
  );
  const [visibleConditions, setVisibleConditions] = useState<Set<number>>(
    new Set(initialVisibleConditions)
  );
  const [lastClickedProperty, setLastClickedProperty] = useState<string | null>(
    null
  );
  const [lastClickedCondition, setLastClickedCondition] = useState<
    number | null
  >(null);

  // Reset state when modal opens. (useEffect, not useMemo — this is a side
  // effect; useMemo here would double-fire setters under React 18 StrictMode.)
  useEffect(() => {
    if (open) {
      setVisibleColumns(new Set(initialVisibleColumns));
      setVisibleConditions(new Set(initialVisibleConditions));
      setSearchTerm("");
      setActiveTab(0);
      setLastClickedProperty(null);
      setLastClickedCondition(null);
    }
  }, [open, initialVisibleColumns, initialVisibleConditions]);

  // Resolve display parts for each property field. The grid view computes
  // these once (including "#N" disambiguation against the real condition
  // tree) and passes them down via `propertyDisplayInfo`, so this modal does
  // not need to parse column keys or count instances itself.
  const propertyInfos: PropertyInfo[] = useMemo(() => {
    const fallback: PropertyDisplayInfo = {
      typeName: "",
      fieldName: "",
      type: "Attribute",
    };
    return allPropertyNames
      .map((field) => {
        const info = propertyDisplayInfo.get(field) ?? {
          ...fallback,
          typeName: field,
        };
        return { field, ...info };
      })
      .sort(
        (a, b) =>
          a.typeName.localeCompare(b.typeName) ||
          a.fieldName.localeCompare(b.fieldName)
      );
  }, [allPropertyNames, propertyDisplayInfo]);

  // Filter properties based on search term
  const filteredProperties = useMemo(() => {
    if (!searchTerm) return propertyInfos;
    const lower = searchTerm.toLowerCase();
    return propertyInfos.filter(
      (prop) =>
        prop.typeName.toLowerCase().includes(lower) ||
        prop.fieldName.toLowerCase().includes(lower)
    );
  }, [propertyInfos, searchTerm]);

  // Filter conditions based on search term
  const filteredConditions = useMemo(() => {
    if (!searchTerm) return conditions;
    return conditions.filter((condition) =>
      condition.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [conditions, searchTerm]);

  const selectedCount =
    activeTab === 0
      ? filteredProperties.filter((prop) => visibleColumns.has(prop.field))
          .length
      : filteredConditions.filter((condition) =>
          visibleConditions.has(condition.id)
        ).length;
  const totalCount =
    activeTab === 0 ? filteredProperties.length : filteredConditions.length;

  const handleSelectAll = () => {
    if (activeTab === 0) {
      const newVisible = new Set(visibleColumns);
      filteredProperties.forEach((prop) => newVisible.add(prop.field));
      setVisibleColumns(newVisible);
    } else {
      const newVisible = new Set(visibleConditions);
      filteredConditions.forEach((condition) => newVisible.add(condition.id));
      setVisibleConditions(newVisible);
    }
  };

  const handleDeselectAll = () => {
    if (activeTab === 0) {
      const newVisible = new Set(visibleColumns);
      filteredProperties.forEach((prop) => newVisible.delete(prop.field));
      setVisibleColumns(newVisible);
    } else {
      const newVisible = new Set(visibleConditions);
      filteredConditions.forEach((condition) =>
        newVisible.delete(condition.id)
      );
      setVisibleConditions(newVisible);
    }
  };

  const handleColumnToggle = (
    columnField: string,
    shiftKey: boolean = false
  ) => {
    const newVisible = new Set(visibleColumns);

    if (shiftKey && lastClickedProperty !== null) {
      // Find indices of current and last clicked items
      const currentIndex = filteredProperties.findIndex(
        (p) => p.field === columnField
      );
      const lastIndex = filteredProperties.findIndex(
        (p) => p.field === lastClickedProperty
      );

      if (currentIndex !== -1 && lastIndex !== -1) {
        const start = Math.min(currentIndex, lastIndex);
        const end = Math.max(currentIndex, lastIndex);

        // Determine if we're selecting or deselecting based on current item's state
        const shouldSelect = !newVisible.has(columnField);

        // Toggle all items in range
        for (let i = start; i <= end; i++) {
          if (shouldSelect) {
            newVisible.add(filteredProperties[i].field);
          } else {
            newVisible.delete(filteredProperties[i].field);
          }
        }
      }
    } else {
      // Normal toggle behavior
      if (newVisible.has(columnField)) {
        newVisible.delete(columnField);
      } else {
        newVisible.add(columnField);
      }
    }

    setVisibleColumns(newVisible);
    setLastClickedProperty(columnField);
  };

  const handleConditionToggle = (
    conditionId: number,
    shiftKey: boolean = false
  ) => {
    const newVisible = new Set(visibleConditions);

    if (shiftKey && lastClickedCondition !== null) {
      // Find indices of current and last clicked items
      const currentIndex = filteredConditions.findIndex(
        (c) => c.id === conditionId
      );
      const lastIndex = filteredConditions.findIndex(
        (c) => c.id === lastClickedCondition
      );

      if (currentIndex !== -1 && lastIndex !== -1) {
        const start = Math.min(currentIndex, lastIndex);
        const end = Math.max(currentIndex, lastIndex);

        // Determine if we're selecting or deselecting based on current item's state
        const shouldSelect = !newVisible.has(conditionId);

        // Toggle all items in range
        for (let i = start; i <= end; i++) {
          if (shouldSelect) {
            newVisible.add(filteredConditions[i].id);
          } else {
            newVisible.delete(filteredConditions[i].id);
          }
        }
      }
    } else {
      // Normal toggle behavior
      if (newVisible.has(conditionId)) {
        newVisible.delete(conditionId);
      } else {
        newVisible.add(conditionId);
      }
    }

    setVisibleConditions(newVisible);
    setLastClickedCondition(conditionId);
  };

  const handleApply = () => {
    onApply(visibleColumns, visibleConditions);
    onClose();
  };

  const handleCancel = () => {
    setSearchTerm("");
    setActiveTab(0);
    setLastClickedProperty(null);
    setLastClickedCondition(null);
    onClose();
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setSearchTerm("");
    setLastClickedProperty(null);
    setLastClickedCondition(null);
  };

  return (
    <Modal open={open} onClose={handleCancel}>
      <ModalContent>
        <ModalHeader>
          <Typography variant="h6">Filter Display Options</Typography>
          <SelectedCountChip
            label={`${selectedCount} / ${totalCount} selected`}
          />
        </ModalHeader>

        <TextField
          fullWidth
          size="small"
          placeholder={
            activeTab === 0
              ? "Search by field name or component..."
              : "Search by condition name..."
          }
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <ButtonGroup>
          <Button color="secondary" onClick={handleSelectAll}>
            SELECT ALL
          </Button>
          <Button color="secondary" onClick={handleDeselectAll}>
            DESELECT ALL
          </Button>
        </ButtonGroup>

        <Box
          sx={{
            borderBottom: 1,
            borderColor: "#e0e0e0",
            backgroundColor: "#fff",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              minHeight: "48px",
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "15px",
                fontWeight: 400,
                color: "#666",
                minHeight: "48px",
                minWidth: "auto",
              },
              "& .Mui-selected": {
                color: "#1976d2 !important",
                fontWeight: 500,
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#1976d2",
                height: "3px",
              },
            }}
          >
            <Tab label="Properties" />
            <Tab label="Conditions" />
          </Tabs>
        </Box>

        <Divider />

        <ColumnList>
          {activeTab === 0
            ? filteredProperties.map((prop) => {
                const isChecked = visibleColumns.has(prop.field);

                return (
                  <ColumnItem
                    key={prop.field}
                    onClick={(e: React.MouseEvent) =>
                      handleColumnToggle(prop.field, e.shiftKey)
                    }
                  >
                    <Checkbox
                      checked={isChecked}
                      color="secondary"
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) =>
                        handleColumnToggle(
                          prop.field,
                          (e.nativeEvent as MouseEvent).shiftKey
                        )
                      }
                    />
                    <ColumnDetails>
                      <Typography variant="body2" fontWeight="medium">
                        {prop.typeName}
                      </Typography>
                      <ColumnType>
                        {prop.fieldName} - {prop.type}
                      </ColumnType>
                    </ColumnDetails>
                  </ColumnItem>
                );
              })
            : filteredConditions.map((condition) => {
                const isChecked = visibleConditions.has(condition.id);

                return (
                  <ColumnItem
                    key={condition.id}
                    onClick={(e: React.MouseEvent) =>
                      handleConditionToggle(condition.id, e.shiftKey)
                    }
                  >
                    <Checkbox
                      checked={isChecked}
                      color="secondary"
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) =>
                        handleConditionToggle(
                          condition.id,
                          (e.nativeEvent as MouseEvent).shiftKey
                        )
                      }
                    />
                    <ColumnDetails>
                      <Typography variant="body2" fontWeight="medium">
                        {condition.name}
                      </Typography>
                      <ColumnType>ID: {condition.id}</ColumnType>
                    </ColumnDetails>
                  </ColumnItem>
                );
              })}
        </ColumnList>

        <ModalFooter>
          <Button color="secondary" onClick={handleCancel}>
            CANCEL
          </Button>
          <Button color="primary" onClick={handleApply}>
            APPLY
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default GridViewFilterModal;
